/**
 * FormStatusCards - TASK-378
 * Four horizontal cards showing form status:
 * KYC Profile, Client Profile, Form A, Risk Profile.
 * Each card is clickable to navigate to the form.
 */

import React from 'react';
import {
  FileText,
  User,
  FileCheck,
  ShieldCheck,
  ChevronRight,
  Lock,
  Loader2,
} from 'lucide-react';
import { useAllFormStatuses } from '../../../hooks/useCompliance';
import type { ComplianceSubTab } from '../ComplianceSubNav';
import type { FormType } from '../../../types/clientCompliance';

interface FormStatusCardsProps {
  clientId: string;
  onNavigateToSubTab: (tab: ComplianceSubTab) => void;
}

interface FormCardConfig {
  formType: FormType;
  label: string;
  icon: React.ElementType;
}

const FORM_CARDS: FormCardConfig[] = [
  { formType: 'kyc', label: 'KYC Profile', icon: FileText },
  { formType: 'clientProfile', label: 'Client Profile', icon: User },
  { formType: 'formA', label: 'Form A', icon: FileCheck },
  { formType: 'riskProfile', label: 'Risk Profile', icon: ShieldCheck },
];

type FormDisplayStatus = 'draft' | 'in_progress' | 'complete' | 'locked' | 'not_started';

const STATUS_CONFIG: Record<
  FormDisplayStatus,
  { label: string; badgeBg: string; badgeText: string; borderColor: string }
> = {
  not_started: {
    label: 'Not Started',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-600',
    borderColor: 'border-gray-200',
  },
  draft: {
    label: 'Draft',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
    borderColor: 'border-amber-200',
  },
  in_progress: {
    label: 'In Progress',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-700',
    borderColor: 'border-blue-200',
  },
  complete: {
    label: 'Complete',
    badgeBg: 'bg-green-100',
    badgeText: 'text-green-700',
    borderColor: 'border-green-200',
  },
  locked: {
    label: 'Locked',
    badgeBg: 'bg-purple-100',
    badgeText: 'text-purple-700',
    borderColor: 'border-purple-200',
  },
};

function mapFormStatus(rawStatus?: string): FormDisplayStatus {
  if (!rawStatus) return 'not_started';

  switch (rawStatus) {
    case 'completed':
    case 'approved':
      return 'complete';
    case 'draft':
      return 'draft';
    case 'in_progress':
    case 'in_review':
    case 'submitted':
    case 'changes_requested':
      return 'in_progress';
    case 'locked':
      return 'locked';
    default:
      return 'not_started';
  }
}

function getStatusIcon(displayStatus: FormDisplayStatus): React.ReactNode {
  if (displayStatus === 'locked') {
    return <Lock className="w-3.5 h-3.5" />;
  }
  if (displayStatus === 'in_progress') {
    return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
  }
  return null;
}

export const FormStatusCards: React.FC<FormStatusCardsProps> = ({ onNavigateToSubTab }) => {
  const formStatuses = useAllFormStatuses();

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {FORM_CARDS.map(({ formType, label, icon: Icon }) => {
        const rawStatus = formStatuses[formType]?.status;
        const displayStatus = mapFormStatus(rawStatus);
        const config = STATUS_CONFIG[displayStatus];
        const statusIcon = getStatusIcon(displayStatus);

        const completedSections = formStatuses[formType]?.completedSections ?? 0;
        const totalSections = formStatuses[formType]?.totalSections ?? 0;
        const progressPct =
          totalSections > 0 ? Math.round((completedSections / totalSections) * 100) : 0;

        return (
          <button
            key={formType}
            onClick={() => {
              onNavigateToSubTab('forms');
            }}
            className={`w-full text-left p-4 rounded-lg border-2 transition-all hover:shadow-md bg-white ${config.borderColor}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">{label}</span>
              </div>
            </div>

            {/* Status badge */}
            <div className="flex items-center gap-1.5 mb-3">
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${config.badgeBg} ${config.badgeText}`}
              >
                {statusIcon}
                {config.label}
              </span>
            </div>

            {/* Progress bar */}
            {totalSections > 0 && displayStatus !== 'not_started' && (
              <div className="mb-2">
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${
                      displayStatus === 'complete'
                        ? 'bg-green-500'
                        : displayStatus === 'in_progress'
                        ? 'bg-blue-500'
                        : 'bg-amber-500'
                    }`}
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {completedSections}/{totalSections} sections
                </p>
              </div>
            )}

            {/* Action */}
            <div className="text-xs text-gray-500 flex items-center gap-1 mt-2">
              {displayStatus === 'not_started' ? 'Start' : 'Open'}
              <ChevronRight className="w-3 h-3" />
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default FormStatusCards;
